import type { ButtonProps } from "@mui/material";

import { Box, Button, Divider, Container } from "@mui/material";
import { JSX } from "react";
import { Copyright } from "../layout/Copyright";
import { useI18n } from "../hooks/useI18n";
import { theme } from "../layout/Theme";

export default function Footer(): JSX.Element {
  const commonSx = {
    borderTop: `1px solid ${theme.palette.neutral[300]}`,
    py: 1,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    fontSize: "0.75rem",
    width: "100%",
  } as const;

  const { t } = useI18n();

  const variantSx = { ...commonSx, mt: "auto" };

  const content = (
    <Box
      component="footer"
      sx={{
        ...variantSx,
        backgroundColor: theme.palette.background.paper,
        display: { xs: "none", sm: "flex" },
        "@media print": { display: "none" },
      }}
    >
      <Container
        maxWidth="lg"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "stretch", gap: 1.5 }}>
          <FooterButton href={`/knowledge`}>{t("footer.knowledge.title")}</FooterButton>

          <Divider
            orientation="vertical"
            flexItem
            sx={{
              borderColor: theme.palette.neutral[300],
              alignSelf: "stretch",
              "@media print": { display: "none" },
            }}
          />

          <FooterButton href="https://support-cvnpro.mesvaccins.net" target="_blank" rel="noopener noreferrer">
            {t("common.helpCenter")}
          </FooterButton>
        </Box>

        <Copyright />
      </Container>
    </Box>
  );

  return content;
}

type FooterButtonProps = Omit<ButtonProps, "variant" | "size"> & {
  target?: string;
  rel?: string;
};

const FooterButton = ({ sx, ...props }: FooterButtonProps) => (
  <Button
    variant="text"
    size="small"
    sx={{
      justifyContent: "flex-start",
      color: theme.palette.neutral[700],
      textTransform: "none",
      fontSize: { xs: 12, md: 14 },
      fontWeight: 400,
      minHeight: "24px",
      py: 0.2,
      "&:hover": {
        color: theme.palette.primary.main,
        background: "transparent",
      },
      ...sx,
    }}
    {...props}
  />
);
